/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Middleware, Store } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { SnackbarModuleStore } from '@opengeoweb/snackbar';
import { snackbarListener, snackbarReducer } from '@opengeoweb/snackbar';
import type {
  DrawtoolModuleStore,
  ReducerMap,
  UIModuleState,
  WebMapStateModuleState,
} from '@opengeoweb/store';
import {
  drawingToolListener,
  drawingToolReducer,
  layerReducer,
  layersListener,
  mapListener,
  mapReducer,
  openlayersListener,
  syncGroupsListener,
  syncGroupsReducer,
  uiReducer,
} from '@opengeoweb/store';
import type { WarningModuleStore } from './types';
import { warningsDrawingsReducer } from './warningsDrawings';
import { warningsFiltersReducer } from './warningsFilters';
import { warningsFormListener, warningsFormReducer } from './warningsForm';

export const warningsReducersMap: ReducerMap = {
  webmap: mapReducer,
  layers: layerReducer,
  ui: uiReducer,
  drawingtools: drawingToolReducer,
  drawings: warningsDrawingsReducer,
  warningsForm: warningsFormReducer,
  warningsFilters: warningsFiltersReducer,
  snackbar: snackbarReducer,
  syncGroups: syncGroupsReducer,
};

export const warningsMiddlewares: Middleware[] = [
  mapListener.middleware,
  layersListener.middleware,
  drawingToolListener.middleware,
  warningsFormListener.middleware,
  snackbarListener.middleware,
  syncGroupsListener.middleware,
  openlayersListener.middleware,
];

const warningsRootReducer = combineReducers(warningsReducersMap);

export const createMockStore = (
  mockState?:
    | WebMapStateModuleState
    | DrawtoolModuleStore
    | UIModuleState
    | WarningModuleStore
    | SnackbarModuleStore,
): Store =>
  configureStore({
    reducer: warningsRootReducer,
    preloadedState: mockState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(warningsMiddlewares),
  });
